// Modal to import a live event exported from another environment. Creates a
// fresh DRAFT with the dates chosen here; reports any content referenced by the
// event that doesn't exist in this environment.

import { useMutation } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import { Alert, Button, Col, Modal, ModalProps, Row, Spinner } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import { applyChangeIfDateValid } from '../../../../../../utils/datepicker';
import { IMPORT_LIVE_EVENT } from '../graphql';

type MissingReference = { kind: string; id: string; source: string };

type Props = {
  onImported: (liveEvent: { id: string; startDate: string; name?: string }) => void;
} & ModalProps;

const ImportLiveEventModal = ({ show, onHide, onImported }: Props) => {
  const [json, setJson] = React.useState('');
  const [startDate, setStartDate] = React.useState<Date>(
    moment().utc().set({ hour: 0, minute: 0, second: 0 }).toDate()
  );
  const [endDate, setEndDate] = React.useState<Date>(
    moment().utc().set({ hour: 0, minute: 0, second: 0 }).add(2, 'days').toDate()
  );
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [missingReferences, setMissingReferences] = React.useState<MissingReference[] | null>(null);
  const [importedName, setImportedName] = React.useState<string | null>(null);

  const [importLiveEvent, { loading }] = useMutation(IMPORT_LIVE_EVENT, {
    onCompleted: (data) => {
      const result = data?.importLiveEvent;
      if (!result?.liveEvent) {
        return;
      }
      setErrorMsg(null);
      setMissingReferences(result.missingReferences ?? []);
      setImportedName(result.liveEvent.name ?? '');
      onImported(result.liveEvent);
    },
    onError: (error) => {
      setMissingReferences(null);
      setImportedName(null);
      setErrorMsg(error.message);
    },
  });

  const resetAndHide = () => {
    setJson('');
    setErrorMsg(null);
    setMissingReferences(null);
    setImportedName(null);
    onHide?.();
  };

  const handleImport = () => {
    setErrorMsg(null);
    setMissingReferences(null);
    setImportedName(null);
    importLiveEvent({
      variables: {
        input: {
          json,
          startDate,
          endDate,
        },
      },
    });
  };

  return (
    <Modal show={show} onHide={resetAndHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Import live event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          Paste the JSON exported from another environment and pick the dates for this environment. A new{' '}
          <strong>draft</strong> event is created — review it before activating.
        </Alert>

        <Row className="mb-3">
          <Col xs={4}>Start date UTC</Col>
          <Col xs={6}>
            <DateTimePicker
              dateFormat="YYYY-MM-DD"
              utc={true}
              timeFormat={true}
              timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
              onChange={applyChangeIfDateValid(setStartDate)}
              initialValue={startDate}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col xs={4}>End date UTC</Col>
          <Col xs={6}>
            <DateTimePicker
              dateFormat="YYYY-MM-DD"
              utc={true}
              timeFormat={true}
              timeConstraints={{ minutes: { min: 0, max: 0, step: 0 } }}
              onChange={applyChangeIfDateValid(setEndDate)}
              initialValue={endDate}
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>Exported JSON</Col>
        </Row>
        <textarea
          className="form-control"
          rows={12}
          value={json}
          onChange={(e) => setJson(e.target.value)}
          placeholder="Paste the exported live event JSON here"
          style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
        />

        {errorMsg && (
          <Alert variant="danger" className="mt-3">
            {errorMsg}
          </Alert>
        )}

        {importedName !== null && (
          <Alert variant="success" className="mt-3">
            Imported draft &quot;{importedName || 'New live event'}&quot; created.
            {missingReferences && missingReferences.length === 0 && ' All referenced content exists here.'}
          </Alert>
        )}

        {missingReferences && missingReferences.length > 0 && (
          <Alert variant="warning" className="mt-3">
            <strong>{missingReferences.length} referenced item(s) do not exist in this environment.</strong> The
            draft was still created — fix these before activating:
            <ul className="mb-0 mt-2">
              {missingReferences.map((ref) => (
                <li key={`${ref.source}-${ref.id}`}>
                  {ref.kind} <code>{ref.id}</code> (from {ref.source})
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={resetAndHide}>
          Close
        </Button>
        <Button variant="primary" disabled={loading || json.trim().length === 0} onClick={handleImport}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Import'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportLiveEventModal;
